import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { Button } from "../components/ui/button";
import { Globe2, Plus, Loader2, MapPin, Building2, Users, Package, RefreshCw, Power, X, Check, ChevronRight, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  live:         "bg-emerald-100 text-emerald-700 border-emerald-200",
  paused:       "bg-amber-100 text-amber-700 border-amber-200",
  provisioning: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function AdminCities() {
  const [cities, setCities] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/cities/");
      setCities(data.cities || []);
      setSummary(data.summary);
    } catch { toast.error("Failed to load cities"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggleStatus = async (c) => {
    const next = c.status === "live" ? "paused" : "live";
    if (!window.confirm(`Move ${c.name} → ${next}?`)) return;
    try {
      await api.post(`/admin/cities/${c.code}/status`, { status: next });
      toast.success(`${c.name} is now ${next}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="admin-cities-page">
      <div className="flex items-center justify-between gap-3 animate-fadeInUp">
        <div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Globe2 className="h-7 w-7 text-[#FF6B00]" /> Cities
          </h1>
          <p className="text-sm text-slate-500 mt-1">Multi-city operations · provision, pause, and audit per-city footprints.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="gap-2"><RefreshCw className="h-4 w-4" /> Refresh</Button>
          <Button onClick={() => setWizardOpen(true)} className="bg-[#FF6B00] hover:bg-orange-600 text-white gap-2" data-testid="cities-provision-btn">
            <Plus className="h-4 w-4" /> Provision new city
          </Button>
        </div>
      </div>

      {/* Summary tiles */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 animate-fadeInUp">
          <Tile label="Total" value={summary.total} />
          <Tile label="Live" value={summary.live} color="text-emerald-700" />
          <Tile label="Paused" value={summary.paused} color="text-amber-700" />
          <Tile label="Provisioning" value={summary.provisioning} color="text-slate-700" />
        </div>
      )}

      {/* Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 animate-fadeInUp" data-testid="cities-grid">
        {loading ? (
          <div className="col-span-full flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#FF6B00]" /></div>
        ) : !cities.length ? (
          <div className="col-span-full bg-white rounded-2xl border border-slate-100 p-12 text-center">
            <Globe2 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-base font-semibold text-slate-700">No cities yet</p>
            <p className="text-sm text-slate-400 mt-1">Click "Provision new city" to launch operations in a new market.</p>
          </div>
        ) : (
          cities.map((c) => (
            <div key={c.city_id} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 hover:shadow-md transition-shadow cursor-pointer"
                 onClick={() => navigate(`/dashboard/cities/${c.code}`)}
                 data-testid={`city-card-${c.code}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-['Outfit'] text-2xl font-extrabold text-slate-900">{c.code}</span>
                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase border ${STATUS_STYLE[c.status] || STATUS_STYLE.provisioning}`}>{c.status}</span>
                  </div>
                  <p className="text-sm font-semibold text-slate-700 mt-0.5">{c.name}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{c.timezone} · {c.currency}</p>
                </div>
                <button onClick={(e) => { e.stopPropagation(); toggleStatus(c); }}
                        className={`p-1.5 rounded-lg transition-colors ${c.status === "live" ? "text-emerald-600 hover:bg-emerald-50" : "text-amber-600 hover:bg-amber-50"}`}
                        title={c.status === "live" ? "Pause city" : "Resume city"}
                        data-testid={`city-toggle-${c.code}`}>
                  <Power className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                <KPI icon={Building2} label="Hubs" value={c.kpis.hub_count} />
                <KPI icon={Package} label="Orders 7d" value={c.kpis.orders_7d} />
                <KPI icon={Users} label="Active agents" value={c.kpis.active_agents} />
                <KPI icon={MapPin} label="Active orders" value={c.kpis.active_orders} />
              </div>
              <p className="text-[10px] text-slate-400 mt-3 flex items-center justify-end">
                Open dashboard <ChevronRight className="h-3 w-3" />
              </p>
            </div>
          ))
        )}
      </div>

      {wizardOpen && <ProvisionWizard onClose={() => setWizardOpen(false)} onCreated={() => { setWizardOpen(false); load(); }} />}
    </div>
  );
}

function Tile({ label, value, color = "text-slate-700" }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-3">
      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{label}</p>
      <p className={`text-2xl font-bold ${color} font-['Outfit']`}>{value}</p>
    </div>
  );
}

function KPI({ icon: Icon, label, value }) {
  return (
    <div className="bg-slate-50 rounded-xl p-2 flex items-center gap-2">
      <Icon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-widest text-slate-400">{label}</p>
        <p className="font-bold text-slate-900 text-sm">{value}</p>
      </div>
    </div>
  );
}

// ───────── 4-step provisioning wizard ─────────
function ProvisionWizard({ onClose, onCreated }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", code: "", timezone: "Asia/Kolkata", currency: "INR",
    feature_flags: { wallet: true, cod: true, hyperlocal: true, surge: false },
    initial_hub: { name: "", lat: "", lng: "", hub_type: "main_hub", capacity: 100 },
    pricing_defaults: { base_price: 99, per_km: 12, gst_pct: 18 },
  });

  const steps = ["Basics", "Features", "Initial hub", "Confirm"];

  const next = () => setStep((s) => Math.min(s + 1, steps.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const canProceed = () => {
    if (step === 0) return form.name.trim().length >= 2;
    if (step === 2) return !!(form.initial_hub.name && form.initial_hub.lat && form.initial_hub.lng);
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(),
        code: form.code.trim().toUpperCase() || undefined,
        timezone: form.timezone, currency: form.currency,
        feature_flags: form.feature_flags,
        pricing_defaults: form.pricing_defaults,
        initial_hub: form.initial_hub.name ? {
          ...form.initial_hub,
          lat: parseFloat(form.initial_hub.lat),
          lng: parseFloat(form.initial_hub.lng),
        } : null,
      };
      const { data } = await api.post("/admin/cities/", body);
      toast.success(`Provisioned ${data.city.name} (${data.city.code})`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Provision failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="provision-wizard">
      <button className="absolute inset-0 bg-slate-900/40" onClick={onClose} aria-label="Close" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto animate-fadeInUp">
        <div className="sticky top-0 bg-white border-b border-slate-100 p-4 flex items-center justify-between z-10">
          <h2 className="font-['Outfit'] text-lg font-bold">Provision new city</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="wizard-close"><X className="h-4 w-4" /></button>
        </div>

        {/* Stepper */}
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-1.5 overflow-x-auto">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center gap-1.5 shrink-0">
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${i < step ? "bg-emerald-500 text-white" : i === step ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-400"}`}>
                {i < step ? <Check className="h-3 w-3" /> : i + 1}
              </span>
              <span className={`text-xs font-semibold ${i === step ? "text-slate-900" : "text-slate-400"}`}>{s}</span>
              {i < steps.length - 1 && <span className="w-3 h-px bg-slate-200" />}
            </div>
          ))}
        </div>

        <div className="p-5 space-y-4">
          {step === 0 && (
            <div className="space-y-3" data-testid="wizard-step-0">
              <Field label="City name *">
                <input type="text" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                       placeholder="e.g. Pune" className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                       data-testid="wizard-name" />
              </Field>
              <Field label="Code (3 letters · auto if blank)">
                <input type="text" value={form.code} maxLength={4}
                       onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                       placeholder="PNQ" className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-mono uppercase"
                       data-testid="wizard-code" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Timezone">
                  <input type="text" value={form.timezone} onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}
                         className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" />
                </Field>
                <Field label="Currency">
                  <input type="text" value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))}
                         className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-mono" />
                </Field>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3" data-testid="wizard-step-1">
              <p className="text-xs text-slate-500">Toggle features for this city. You can change these later.</p>
              {Object.entries(form.feature_flags).map(([key, val]) => (
                <label key={key} className="flex items-center justify-between p-3 rounded-xl bg-slate-50 cursor-pointer">
                  <span className="text-sm font-medium text-slate-900 capitalize">{key.replace(/_/g, " ")}</span>
                  <input type="checkbox" checked={val}
                         onChange={(e) => setForm((f) => ({ ...f, feature_flags: { ...f.feature_flags, [key]: e.target.checked } }))}
                         className="h-4 w-4"
                         data-testid={`wizard-flag-${key}`} />
                </label>
              ))}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3" data-testid="wizard-step-2">
              <p className="text-xs text-slate-500">Create the city's first main hub. You can add more later from /dashboard/hubs.</p>
              <Field label="Hub name *">
                <input type="text" value={form.initial_hub.name}
                       onChange={(e) => setForm((f) => ({ ...f, initial_hub: { ...f.initial_hub, name: e.target.value } }))}
                       placeholder="e.g. Pune Main Hub" className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm"
                       data-testid="wizard-hub-name" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Latitude *">
                  <input type="number" step="0.000001" value={form.initial_hub.lat}
                         onChange={(e) => setForm((f) => ({ ...f, initial_hub: { ...f.initial_hub, lat: e.target.value } }))}
                         placeholder="18.5204" className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-mono"
                         data-testid="wizard-hub-lat" />
                </Field>
                <Field label="Longitude *">
                  <input type="number" step="0.000001" value={form.initial_hub.lng}
                         onChange={(e) => setForm((f) => ({ ...f, initial_hub: { ...f.initial_hub, lng: e.target.value } }))}
                         placeholder="73.8567" className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-mono"
                         data-testid="wizard-hub-lng" />
                </Field>
              </div>
              <Field label="Capacity (orders/day)">
                <input type="number" min="10" value={form.initial_hub.capacity}
                       onChange={(e) => setForm((f) => ({ ...f, initial_hub: { ...f.initial_hub, capacity: parseInt(e.target.value || "100", 10) } }))}
                       className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" />
              </Field>
              <p className="text-[10px] text-slate-400 italic">Note: city_manager assignment is deferred until that role has real users.</p>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3" data-testid="wizard-step-3">
              <p className="text-xs text-slate-500">Review and provision.</p>
              <div className="bg-slate-50 rounded-xl p-4 space-y-1.5 text-sm">
                <Row label="City" value={`${form.name} (${form.code || "auto"})`} />
                <Row label="Timezone / Currency" value={`${form.timezone} · ${form.currency}`} />
                <Row label="Features" value={Object.entries(form.feature_flags).filter(([, v]) => v).map(([k]) => k).join(", ") || "—"} />
                {form.initial_hub.name && (
                  <Row label="Initial hub" value={`${form.initial_hub.name} @ ${form.initial_hub.lat}, ${form.initial_hub.lng} (cap ${form.initial_hub.capacity})`} />
                )}
              </div>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-2.5">
                Will set <b>status=live</b> immediately. You can pause from the dashboard if needed.
              </p>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-100 p-4 flex items-center justify-between">
          <Button variant="outline" onClick={prev} disabled={step === 0} className="gap-1" data-testid="wizard-back">
            <ChevronLeft className="h-4 w-4" /> Back
          </Button>
          {step < steps.length - 1 ? (
            <Button onClick={next} disabled={!canProceed()} className="bg-slate-900 hover:bg-slate-800 text-white gap-1" data-testid="wizard-next">
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={submit} disabled={busy} className="bg-[#FF6B00] hover:bg-orange-600 text-white gap-1" data-testid="wizard-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Provision city
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-slate-500">{label}</span>
      <span className="font-semibold text-slate-900 text-right">{value}</span>
    </div>
  );
}
